/* eslint-disable react/require-default-props */

import React, { useEffect, useContext, useState } from 'react';
import proptypes from 'prop-types';
const { bool, string, arrayOf } = proptypes;

import { useConfigService } from '@thd-nucleus/experience-context';
import { extend, QueryProvider, QueryContext, useDataModel } from '@thd-nucleus/data-sources';
import { ImpressionProvider } from '@thd-olt-component-react/impression';

import { BundlesDataModel } from '../models/dataModel';
import {
  filterHiddenDiscontinuedProducts,
  getBundleFeatures,
  getBundleType,
  getComponentImpressionData,
  getExperienceType,
  getUpdatedBundle,
  getLimitedItemIds
} from '../utils/product-utils';
import { getTemplate, getNormalizedProps } from '../subcomponents/templates/index';
import { BUNDLE_PAGE_SIZE } from '../constants/index';

const BundleMini = ({ bundleIds = [], name = '', noPipSpacing = false }) => {
  const { defaultVariables } = useContext(QueryContext);
  const configExperienceType = useConfigService('bundleExperience') || null;
  const configBundleType = useConfigService('bundleType') || null;
  const configMultiParentEnabled = useConfigService('multiParentEnabled') || null;
  const [offsetBundleIds, setOffsetBundleIds] = useState(0);
  const limitedBundleIds = getLimitedItemIds(bundleIds, offsetBundleIds);

  const { data, loading } = useDataModel('products', {
    variables: {
      itemIds: limitedBundleIds
    },
    skip: limitedBundleIds.length === 0
  });

  const bundles = filterHiddenDiscontinuedProducts(data?.products) || [];

  const triggerErrorEvent = () => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-mini.error');
  };

  const noBundles = !loading && bundles.length === 0;
  const hasMoreBundles = offsetBundleIds + BUNDLE_PAGE_SIZE < bundleIds.length;
  const canPaginate = hasMoreBundles && offsetBundleIds === 0;

  useEffect(() => {
    if (noBundles && canPaginate) {
      setOffsetBundleIds((prev) => prev + BUNDLE_PAGE_SIZE);
    } else if (noBundles) {
      triggerErrorEvent();
    }
  }, [noBundles, canPaginate]);

  // Don't render if no bundles and no more to try
  if (noBundles && !canPaginate) {
    return null;
  }

  if (loading) {
    return null;
  }

  const experienceType = configExperienceType || getExperienceType(bundles?.[0]);
  const bundleType = configBundleType || getBundleType(bundles?.[0]);

  const bundleFeatures = getBundleFeatures(bundleType, experienceType);
  const updatedBundles = bundles?.map((bundle) => getUpdatedBundle(bundle, bundleFeatures));

  const { singleParent, multiParent } = bundleFeatures?.templates || {};
  const showMultiParent = configMultiParentEnabled && multiParent && updatedBundles?.length > 1;

  const templateName = showMultiParent ? multiParent : singleParent;
  const ItemTemplate = getTemplate(templateName);

  if (!ItemTemplate) {
    return null;
  }

  const normalizedProps = getNormalizedProps({
    bundles: updatedBundles,
    isMultiParent: showMultiParent,
    bundleLoading: loading,
    onError: triggerErrorEvent,
    noPipSpacing
  });

  return (
    <ImpressionProvider
      data={getComponentImpressionData({
        bundles: updatedBundles,
        slotId: name
      })}
    >
      <QueryProvider
        cacheKey={`${showMultiParent ? 'bundle-mini-multi-parent' : 'bundle-mini-item'}-${templateName}`}
        defaultVariables={defaultVariables?.current}
        dataSource={showMultiParent ? 'MultiParentCarousel' : 'bundleMiniItem'}
      >
        <ItemTemplate
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...normalizedProps}
        />
      </QueryProvider>
    </ImpressionProvider>
  );
};

BundleMini.propTypes = {
  bundleIds: arrayOf(string.isRequired),
  name: string,
  noPipSpacing: bool
};

BundleMini.displayName = 'BundleMini';

BundleMini.dataModel = extend(
  BundlesDataModel
);

export { BundleMini };